"use client";

import { Fragment, useEffect, useState, useRef, useCallback } from 'react'
import { Dialog, Disclosure, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/20/solid'
import Image from "next/image";
import Link from "next/link";
import Pagination from "@/components/shared/pagination"
import { useRouter } from 'next/navigation';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function OurProductsBody(props: any) {
  
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  const { 
    meta,
    slug,
    category,
    products,
    categories,
    page,
  } = props;

  const [pageData, setPageData] = useState(page)
  const router = useRouter();
  const loadedRef = useRef(false);

  const navigate = useCallback((pageData) => {
    router.push(
      `/catalog/${slug}?page=${pageData}`
    );
  }, [router, slug]);

  useEffect(() => {
    if (loadedRef.current) {
      navigate(pageData);
    } else {
      loadedRef.current = true;
    }
  }, [pageData, navigate]);

  const handlePageChange = ({ selected }) => {
    const newPageData = selected + 1
    if(newPageData == pageData ) return;
    setPageData(newPageData)
  };

  const breadcrumbStructuredData = category ? 
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': "ListItem",
        position: 1,
        name: "Custom Product",
        item: "https://mf.asia/our-products"
      },
      {
        '@type': "ListItem",
        position: 2,
        name: category.title,
        item: category.slug
      }]
    } : {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': "ListItem",
        position: 1,
        name: "Custom Product",
        item: "https://mf.asia/our-products"
      }]
    }


  return (
    <>
      <div>
        {/* Mobile filter dialog */}
        <Transition.Root show={mobileFiltersOpen} as={Fragment}>
          <Dialog as="div" className="relative z-40 sm:hidden __variable_89a9b2 font-sans" onClose={setMobileFiltersOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-6 shadow-xl font-sans">
                  <div className="flex items-center justify-between px-4">
                    <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                    <button
                      type="button"
                      className="-mr-2 flex h-10 w-10 items-center justify-center p-2 text-gray-400 hover:text-gray-500"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      <span className="sr-only">Close menu</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Filters */}
                  <form className="mt-4">
                    <Disclosure as="div" defaultOpen className="border-t border-gray-200 pt-4 pb-4">
                      {({ open }) => (
                        <fieldset>
                          <legend className="w-full px-2">
                            <Disclosure.Button className="flex w-full items-center justify-between p-2 text-gray-400 hover:text-gray-500">
                              <span className="text-sm font-medium text-gray-900"> Categories </span>
                              <span className="ml-6 flex h-7 items-center">
                                <ChevronDownIcon
                                  className={classNames(open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform')}
                                  aria-hidden="true"
                                />
                              </span>
                            </Disclosure.Button>
                          </legend>
                          <Disclosure.Panel className="px-4 pb-2">
                            <div className="space-y-6">
                              <ul role="list" className="pt-2 space-y-4 border-gray-200 text-sm font-medium text-gray-900">
                                {categories.map((category) => (
                                  <li key={category.slug}>
                                    <Link href={`/our-products/${category.slug}`} className="text-sm text-gray-600">
                                      {category.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </Disclosure.Panel>
                        </fieldset>
                      )}
                    </Disclosure>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        <main className="mx-auto max-w-2xl px-4 lg:max-w-7xl lg:px-8">
          <div className="border-b border-gray-200 pt-24 pb-10">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900"> { category.title } </h1>
            <p className="mt-4 text-base text-gray-500">
              { category.description || "From custom t-shirts and workwear to promotional products and even embroidery, MF has the perfect product for your team, school, company or event." }
            </p>
          </div>

          <div className="pt-12 pb-24 sm:grid sm:grid-cols-4 lg:grid-cols-4 sm:gap-x-4 lg:gap-x-8 xl:grid-cols-6">
            <aside>
              <h2 className="sr-only">Filters</h2>

              <button
                type="button"
                className="inline-flex items-center sm:hidden"
                onClick={() => setMobileFiltersOpen(true)}
              >
                <span className="text-sm font-medium text-gray-700">Filters</span>
                <PlusIcon className="ml-1 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              </button>

              <div className="hidden sm:block">
                <form className="space-y-5 divide-y divide-gray-200">
                  <div>
                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-900"> Categories </legend>
                      <ul role="list" className="pt-6 space-y-4 border-gray-200 text-sm font-medium text-gray-900">
                        <li key='all-products'>
                          <Link href={`/our-products`} className={`${slug == '' ? 'text-blue-600' : 'text-gray-600'} text-sm`}>
                            All Products
                          </Link>
                        </li>
                        {categories.map((category) => (
                          <li key={category.slug}>
                            <Link href={`/our-products/${category.slug}`} className={`${slug == category.slug ? 'text-blue-600' : 'text-gray-600'} text-sm`}>
                              {category.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </fieldset>
                  </div>
                </form>
              </div>
            </aside>

            {products && products.length > 0 ? <section aria-labelledby="product-heading" className="mt-6 sm:col-span-3 lg:col-span-3 lg:mt-0 xl:col-span-5">
              <h2 id="product-heading" className="sr-only">
                Products
              </h2>

              <div className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3 xs:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:gap-x-6 xl:grid-cols-4">
                {products.map((product) => (
                  <div
                    key={product.slug}
                    className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
                  >
                    <div className="aspect-w-3 aspect-h-3 bg-gray-200 group-hover:opacity-75 sm:aspect-none">
                      {product.imageSrc && <Image
                        src={product.imageSrc || ''}
                        alt={product.name}
                        width={512}
                        height={512}
                        className="h-full w-full object-cover object-center sm:h-full sm:w-full"
                      />}
                    </div>
                    <div className="flex flex-1 flex-col space-y-2 p-4">
                      <h3 className="text-sm font-medium text-gray-900">
                        <Link href={`/custom-products/${product.slug}`}>
                          <span aria-hidden="true" className="absolute inset-0" />
                          {product.name}
                        </Link>
                      </h3>
                      {/* <p className="text-sm text-gray-500">{product.description}</p> */}
                      {/* <div className="flex flex-1 flex-col justify-end">
                        <p className="text-sm italic text-gray-500">{product.options}</p>
                        <p className="text-base font-medium text-gray-900">{product.price}</p>
                      </div> */}
                    </div>
                  </div>
                ))}
              </div>
              <Pagination currentPage={ page }
                totalCount={meta.total_count}
                pageSize={meta.per_page}
                onPageChange={handlePageChange}
              />
            </section> : <p className="text-gray-900"> No Products Yet </p>}
          </div>
        </main>
      </div>
    </>
  )
}